import { createTheme as MuiCreateTheme } from '@material-ui/core';
import WebFont from 'webfontloader';
import { isObee } from '../utils/constants';
import { isEmpty } from '../utils/helpers';
import { baseColors, baseFontWeights, baseTypography } from './base';
import { fontFamily as eatClubFontFamily, eatclubColors, eatclubTypography } from './eatclub';
import { obeeColors, fontFamily as obeeFontFamily, obeeTypography } from './obee';

export const colors = { ...baseColors, ...(isObee ? obeeColors : eatclubColors) };
export const fontWeights = baseFontWeights;
export const typography = { ...baseTypography, ...(isObee ? obeeTypography : eatclubTypography) };

const convertPercentageToHex = (percentage) => {
  const percentageAsInt = parseInt(`${percentage}`, 10);

  if (Number.isNaN(percentageAsInt)) {
    return '';
  }
  if (percentageAsInt === 100) {
    return 'ff';
  }
  return Math.floor((256 * percentageAsInt) / 100)
    .toString(16)
    .padStart(2, '0');
};

export const getHexColourWithOpacity = (hexColour, opacity) => {
  return `${hexColour}${convertPercentageToHex(opacity)}`;
};

// TODO at this point all the custom theming should just come in as an object
export const createTheme = (
  generalFontFamilies = null,
  availabilityFontFamilies = null,
  mainButtonColour = colors.liteAvailabilityMainButtonBgColour,
  mainButtonTextColour = null,
  mainButtonCornerRadius = 6,
  mainTextColour = '#313131',
  outlineColour = '#ffffff',
  activeOutlineColour = '#000000', // when hovered or focused
  availabilityBackgroundBoxBgColour = null,
  darkMode = false,
  widgetHeaderBgColor,
  secondaryTextColour,
  secondaryTextOpacity,
  outlineOpacity,
  availabilityFieldsActiveStateColour,
  availabilityFieldsColourOpacity,
  availabilityFieldsOutlineColour,
  availabilityFieldsActiveOutlineColour,
  formHeaderBackgroundColor,
  formHeaderUnderlineColor,
  experienceActiveBackgroundColor = '#E2E8DD',
  experienceActiveTextColor = '#313131',
  dietaryTagBackgroundColor = 'transparent',
  dietaryTagTextColor = null,
  dietaryTagBorderColor = null,
  dietaryTagActiveBackgroundColor = null,
  dietaryTagActiveTextColor = null,
  dietaryTagActiveBorderColor = null,
  restaurantData, // Since we're passing in a million props now
) => {
  const families = [
    'Open Sans',
    ...(generalFontFamilies ? generalFontFamilies.split(',') : []),
    ...(availabilityFontFamilies ? availabilityFontFamilies.split(',') : []),
    ...(restaurantData?.numberFontFamilies ? availabilityFontFamilies.split(',') : []),
  ].filter((value, index, self) => self.indexOf(value) === index);

  const customFonts = restaurantData?.customFonts;
  const loader = {};
  if (!isEmpty(customFonts)) {
    const usableFonts = customFonts.filter((font) => font?.defaultFont);

    loader.custom = {
      families: usableFonts?.map((font) => font?.fontName),
      urls: usableFonts?.map((font) => font?.fontSrc),
    };
  }

  if (families.length > 0) {
    loader.google = { families: families.map((family) => `${family}:400,500,700,900`) };
  }

  // Load all google fonts and custom fonts
  if (!isEmpty(loader)) {
    WebFont.load(loader);
  }

  const activeBackgroundColor = getHexColourWithOpacity(
    availabilityFieldsActiveStateColour,
    availabilityFieldsColourOpacity,
  );

  const availabilityFontFamily =
    availabilityFontFamilies ??
    (isObee ? obeeFontFamily : eatClubFontFamily) ??
    generalFontFamilies;

  return MuiCreateTheme({
    colors: {
      ...colors,
      mainTextColour,
      availabilityBackgroundBoxBgColour,
      darkMode,
      mainButtonTextColour,
      outlineColour: getHexColourWithOpacity(outlineColour, outlineOpacity),
      activeOutlineColour,
      availabilityFieldsOutlineColour:
        availabilityFieldsOutlineColour ?? getHexColourWithOpacity(outlineColour, outlineOpacity),
      availabilityFieldsActiveOutlineColour:
        availabilityFieldsActiveOutlineColour ?? activeOutlineColour,
      // eslint-disable-next-line no-nested-ternary
      formHeaderBackgroundColor: !isEmpty(formHeaderBackgroundColor)
        ? formHeaderBackgroundColor
        : darkMode
        ? 'transparent'
        : widgetHeaderBgColor,
      formHeaderUnderlineColor:
        formHeaderUnderlineColor ?? getHexColourWithOpacity(outlineColour, outlineOpacity),
      inputFieldsBackground: darkMode ? availabilityBackgroundBoxBgColour : 'white',
      secondaryTextColour: getHexColourWithOpacity(secondaryTextColour, secondaryTextOpacity),
      // activeFieldsBackground: getHexColourWithOpacity(
      //   availabilityFieldsActiveStateColour,
      //   availabilityFieldsColourOpacity,
      // ),
      activeFieldsBackground: activeBackgroundColor,
      experienceActiveBackgroundColor,
      experienceActiveTextColor,

      // Dietary tag colours use the time button colours by default
      dietaryTagBackgroundColor,
      dietaryTagTextColor: dietaryTagTextColor ?? secondaryTextColour,
      dietaryTagBorderColor: dietaryTagBorderColor ?? colors.cloud,
      dietaryTagActiveBackgroundColor: dietaryTagActiveBackgroundColor ?? activeBackgroundColor,
      dietaryTagActiveTextColor: dietaryTagActiveTextColor ?? mainTextColour,
      // dietaryTagActiveTextColor ?? getContrastTextColor(activeBackgroundColor),
      dietaryTagActiveBorderColor: dietaryTagActiveBorderColor ?? colors.charcoal,
      dividerColor: restaurantData?.dividerColor,
      availabilityMainButtonBgColour: restaurantData?.availabilityMainButtonBgColour,
      availabilityMainButtonTextColour: restaurantData?.availabilityMainButtonTextColour,
      availabilityMainButtonCornerRadius: restaurantData?.availabilityMainButtonCornerRadius,
    },
    fontWeights,
    baseTypography: {
      ...baseTypography,
      fontFamily: 'inherit',
      availabilityFontFamily,
      generalFontFamily: generalFontFamilies,
      numberFontFamily: restaurantData?.numberFontFamilies || 'Inter' || generalFontFamilies,
    },
    typography: {
      ...typography,
      fontFamily: 'inherit',
    },
    breakpoints: {
      values: {
        xs: 480,
        sm: 644,
        md: 944,
        lg: 1200,
        xl: 1536,
      },
    },
    overrides: {
      MuiInputBase: {
        root: {
          fontSize: '16px', // '1em',
          color: `${mainTextColour} !important`,
        },
      },
      MuiTypography: {
        colorTextSecondary: {
          color: `${mainTextColour}`,
        },
      },
      MuiInputLabel: {
        root: {
          fontSize: '16px', // '1em',
          color: `${secondaryTextColour} !important`,
          '&$focused': {
            color: `${secondaryTextColour} !important`,
          },
        },
      },
      MuiFilledInput: {
        root: {
          // TODO only use coloured background on dark mode
          backgroundColor: availabilityBackgroundBoxBgColour || 'white',
          '&:hover': {
            backgroundColor: availabilityBackgroundBoxBgColour || 'white',
            outline: `1px solid ${activeOutlineColour} !important`,
          },
          '&.Mui-focused': {
            backgroundColor: availabilityBackgroundBoxBgColour || 'white',
            outline: `1px solid ${activeOutlineColour} !important`,
          },
        },
        multiline: {
          padding: '35px 12px 10px',
          minHeight: '200px',
        },
      },
      MuiFormHelperText: {
        root: {
          margin: '4px 0 0 0',
        },
        contained: {
          color: mainTextColour,
          marginLeft: 0,
          marginRight: 0,
          marginTop: 0,
        },
      },
      MuiIconButton: {
        root: {
          color: mainTextColour,
          '&.Mui-disabled': {
            color: '#00000022',
            border: '1px solid transparent',
          },
        },
      },
      MuiButton: {
        root: {
          color: mainTextColour,
        },
        contained: {
          border: `1px solid transparent`,
          '&:focus': {
            border: `1px solid ${activeOutlineColour}`,
          },
        },
        outlined: {
          border: `1px solid ${getHexColourWithOpacity(outlineColour, outlineOpacity)}`,
          '&:hover': {
            border: `1px solid ${activeOutlineColour}`,
          },
          '&:focus': {
            border: `1px solid ${activeOutlineColour}`,
          },
          borderRadius: `${mainButtonCornerRadius}px`,
        },
        containedPrimary: {
          color: mainButtonTextColour,
          borderRadius: `${mainButtonCornerRadius}px !important`,
        },
      },
    },
    palette: {
      primary: {
        main: mainButtonColour,
      },
    },
  });
};
